/**
 * <kashira-counters> — counts numeric stat values up once when the band
 * scrolls into the viewport. Non-numeric values are left untouched, and
 * reduced-motion users get the final value immediately (spec 08 b).
 */
class KashiraCounters extends HTMLElement {
  connectedCallback() {
    this.counters = Array.from(this.querySelectorAll('[data-kashira-counter]'));
    if (!this.counters.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches || typeof IntersectionObserver === 'undefined') return;

    /* The real value stays in the markup until the count actually starts.
       This used to reset every counter to 0 on connect and rely on the
       observer to bring it back, so any visitor, crawler or screen reader the
       observer never reached was told the shop offers "0 days to return".
       run() starts from 0 on its own first frame; nothing is gained by
       zeroing early.

       A band already on screen before the visitor has scrolled keeps its
       numbers standing, as the product-card reveal does (animations.js).
       Threshold 0 starts the count as the band's first pixel arrives, so the
       drop to 0 happens before there is anything to see. */
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          if ((window.scrollY || document.documentElement.scrollTop || 0) <= 0) return;
          this.counters.forEach((el) => this.run(el));
        });
      },
      { threshold: 0 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  run(el) {
    const target = this.constructor.parse(el.dataset.value);
    if (target === null) return;

    const duration = 600;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = this.constructor.format(el.dataset.value, Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(step);
      else el.textContent = el.dataset.value;
    };
    requestAnimationFrame(step);
  }

  /** Leading integer of the value, or null when the value is not countable. */
  static parse(value) {
    const match = String(value || '').match(/\d[\d,]*/);
    if (!match) return null;
    const number = parseInt(match[0].replace(/,/g, ''), 10);
    return Number.isNaN(number) ? null : number;
  }

  /** Rebuild the display string, swapping the number but keeping any prefix/suffix. */
  static format(value, number) {
    const raw = String(value || '');
    const match = raw.match(/\d[\d,]*/);
    if (!match) return raw;
    return raw.replace(match[0], number.toLocaleString());
  }
}

if (!customElements.get('kashira-counters')) {
  customElements.define('kashira-counters', KashiraCounters);
}
